import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { Fragment, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import compose from 'recompose/compose'
import SizeTable from 'components/common/size-table'
import ResponsiveImage from 'components/images/responsive-image'
import { PDP_INTERACT_ELEM_EVENT_NAME } from 'components/pdp/const'
import { MEASUREMENT_DATA } from 'components/pdp/interactions/measurements/const'
import SizesGuidesUnits from 'components/pdp/interactions/measurements/sizes-guide-units'
import {
  getTableColumns,
  getTableRows,
  translateMeasurementType,
} from 'components/pdp/interactions/measurements/utils'
import env from 'config/env'
import Typo from 'constants/typography'
import { withI18next } from 'lib/i18n/withI18next'
import { hasLocizeTranslation } from 'lib/utils/locize'
import { segmentTrackPDPInteract } from 'lib/utils/product'
import styles from './styles.scss'

const SingleTableSection = ({
  internationalization: { country },
  openModal,
  product,
  t,
  sizeHeader,
}) => {
  const [unit, setUnit] = useState('in')
  const {
    attributes,
    is_new_size_method: isNewSize = false,
    measurement_image,
    measurement_type,
    metadata,
  } = product
  const displaySizeGuideCta =
    measurement_type === 'body' && metadata?.size_recommendable

  const rows = useMemo(
    () => getTableRows(attributes, t, measurement_type, unit),
    [attributes, measurement_type, t, unit],
  )
  const columns = useMemo(
    () => getTableColumns(rows, sizeHeader),
    [rows, sizeHeader],
  )

  return (
    <Fragment>
      <style jsx>{styles}</style>
      {displaySizeGuideCta && (
        <button
          className={clsx('pdp-measurements__inter-sizing', Typo.overline)}
          onClick={() => {
            segmentTrackPDPInteract(
              product,
              country,
              PDP_INTERACT_ELEM_EVENT_NAME.interSizing,
            )
            openModal(true)
          }}
        >
          {hasLocizeTranslation(
            t,
            MEASUREMENT_DATA.measurementTitle.titleKey,
            MEASUREMENT_DATA.measurementTitle.default,
          )}
        </button>
      )}
      <SizesGuidesUnits unit={unit} onClick={(val) => setUnit(val)} t={t} />
      <div className={clsx('pdp-measurements__title', Typo.subtitle2)}>
        {translateMeasurementType(measurement_type, t)}
      </div>
      <div className="pdp-measurements__table">
        <SizeTable
          columns={columns}
          rows={rows}
          isNewSize={isNewSize}
          isPhone
        />
      </div>
      {!displaySizeGuideCta && (
        <ResponsiveImage
          src={`${env.IMG_HOST}${measurement_image?.path}`}
          tablet="40vw"
          laptop="40vw"
          desktop="40vw"
          bigDesktop="40vw"
        />
      )}
      {isNewSize && (
        <div className={clsx('size-guide-text', Typo.body2)}>
          {hasLocizeTranslation(
            t,
            MEASUREMENT_DATA.resizeDataText.titleKey,
            MEASUREMENT_DATA.resizeDataText.default,
          )}
        </div>
      )}
    </Fragment>
  )
}

SingleTableSection.defaultProps = { sizeHeader: null }

SingleTableSection.propTypes = {
  sizeHeader: PropTypes.shape({
    key: PropTypes.string,
    title: PropTypes.string,
    titleKey: PropTypes.string,
  }),
  internationalization: PropTypes.shape({
    country: PropTypes.string,
  }).isRequired,
  openModal: PropTypes.func.isRequired,
  product: PropTypes.shape({
    attributes: PropTypes.arrayOf(
      PropTypes.shape({
        measurements: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            value: PropTypes.string,
            value_in: PropTypes.string,
          }),
        ),
      }),
    ),
    category: PropTypes.shape({
      id_category: PropTypes.number,
      link_rewrite: PropTypes.string,
    }),
    id_product: PropTypes.number,
    is_new_size_method: PropTypes.bool,
    measurement_image: PropTypes.shape({
      path: PropTypes.string,
    }),
    measurement_type: PropTypes.string,
    metadata: PropTypes.shape({
      size_recommendable: PropTypes.bool,
    }),
    model: PropTypes.shape({
      bust: PropTypes.number,
      height: PropTypes.number,
      hips: PropTypes.number,
      size: PropTypes.string,
      waist: PropTypes.number,
    }),
    prices: PropTypes.shape({
      original_price: PropTypes.number,
    }),
  }).isRequired,
  t: PropTypes.func.isRequired,
}

export default compose(
  connect((state) => ({
    internationalization: state.internationalization,
  })),
  withI18next(),
)(SingleTableSection)
